import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Loader2, ArrowLeft, Sparkles, CheckCircle2, XCircle, Clock,
    ChevronRight, BarChart3, TrendingUp, TrendingDown, Minus,
    AlertTriangle, ThumbsUp, MessageSquare, CalendarPlus, FileText,
    ChevronDown, ChevronUp, User, Briefcase, Download,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import OfferLetterModal from '@/components/OfferLetterModal';

function ConsolidatedReportSkeleton() {
    return (
        <div className="space-y-6">
            {/* Back button + header */}
            <div className="flex items-center gap-3">
                <Skeleton className="h-9 w-20 rounded-md" />
                <div className="space-y-1.5 flex-1">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-3.5 w-32" />
                </div>
                <Skeleton className="h-9 w-28 rounded-md" />
                <Skeleton className="h-9 w-28 rounded-md" />
            </div>

            {/* Candidate info card */}
            <Card>
                <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <Skeleton className="h-14 w-14 rounded-full shrink-0" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-5 w-44" />
                            <Skeleton className="h-3.5 w-56" />
                            <Skeleton className="h-3.5 w-36" />
                        </div>
                        <div className="flex gap-2">
                            <Skeleton className="h-7 w-20 rounded-full" />
                            <Skeleton className="h-7 w-24 rounded-full" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Round cards */}
            <div className="space-y-3">
                <Skeleton className="h-4 w-32" />
                {Array.from({ length: 2 }).map((_, i) => (
                    <Card key={i}>
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between gap-4">
                                <div className="space-y-2 flex-1">
                                    <div className="flex items-center gap-2">
                                        <Skeleton className="h-5 w-5 rounded-full" />
                                        <Skeleton className="h-4 w-28" />
                                        <Skeleton className="h-5 w-16 rounded-full" />
                                    </div>
                                    <Skeleton className="h-3 w-48" />
                                    <Skeleton className="h-3 w-64" />
                                </div>
                                <Skeleton className="h-12 w-20 rounded-md shrink-0" />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* AI Report card */}
            <Card>
                <CardHeader>
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-3.5 w-64" />
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Skill bars */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="space-y-1.5">
                            <div className="flex justify-between">
                                <Skeleton className="h-3 w-32" />
                                <Skeleton className="h-3 w-10" />
                            </div>
                            <Skeleton className="h-2.5 w-full rounded-full" />
                        </div>
                    ))}
                    <Separator />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-5/6" />
                    <Skeleton className="h-3 w-4/6" />
                </CardContent>
            </Card>
        </div>
    );
}

const DECISION_COLORS = {
    hire:     'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20',
    reject:   'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/20',
    hold:     'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20',
    on_hold:  'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20',
    pending:  'bg-muted text-muted-foreground',
};

const ROUND_STATUS_COLORS = {
    passed:      'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20',
    failed:      'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/20',
    completed:   'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/20',
    in_progress: 'bg-blue-300/15 text-blue-600 border-blue-300/30',
    scheduled:   'bg-muted text-muted-foreground',
    cancelled:   'bg-gray-200/50 text-gray-500',
    terminated:  'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30',
    expired:     'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30',
    no_show:     'bg-gray-500/15 text-gray-600 border-gray-500/30',
};

/** Terminal statuses — round is fully evaluated */
const TERMINAL = new Set(['passed', 'failed', 'cancelled', 'terminated']);

/**
 * Return the latest attempt per roundNumber, sorted by roundNumber ascending.
 * Handles non-sequential roundNumbers (e.g. Round 1 = rn 1, Final Round = rn 4 in old data).
 */
function latestPerRoundNumber(rounds = []) {
    const map = {};
    for (const r of rounds) {
        const rn   = r.roundNumber;
        const prev = map[rn];
        if (!prev || (r.attemptNumber || 1) >= (prev.attemptNumber || 1)) map[rn] = r;
    }
    return Object.values(map).sort((a, b) => a.roundNumber - b.roundNumber);
}

function ScoreBar({ score, passMark }) {
    const pct = Math.min(100, Math.max(0, score ?? 0));
    const passed = score >= passMark;
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <span className={passed ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>
                    {score != null ? `${Math.round(score)}%` : '—'}
                </span>
                <span className="text-muted-foreground">Pass mark: {passMark}%</span>
            </div>
            <div className="relative h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                    className={cn('h-full rounded-full transition-all', passed ? 'bg-green-500' : 'bg-red-400')}
                    style={{ width: `${pct}%` }}
                />
                {passMark && (
                    <div
                        className="absolute top-0 bottom-0 w-0.5 bg-muted-foreground/50"
                        style={{ left: `${passMark}%` }}
                    />
                )}
            </div>
        </div>
    );
}

// ── Round Card with inline pass / fail action ─────────────────────────────────
function RoundCard({ round, processId, onDecisionSaved, isCurrentRound }) {
    const r = round;
    const displayScore = r.overallScore ?? r.score;
    const hasReportData = r.interviewSummary || r.strengths?.length || r.areasForImprovement?.length;
    const dateStr = r.completedAt
        ? `Completed ${new Date(r.completedAt).toLocaleDateString()}`
        : r.scheduledAt
            ? `Scheduled ${new Date(r.scheduledAt).toLocaleDateString()}`
            : '';

    // Per-round decision UI state
    const isTerminal    = TERMINAL.has(r.status);
    // Can act if session is completed/terminated but round not yet decided
    const canAct        = !isTerminal && ['completed', 'terminated'].includes(r.sessionStatus || r.status);
    const [showActions, setShowActions] = useState(canAct);
    const [remark,      setRemark]      = useState('');
    const [submitting,  setSubmitting]  = useState('');   // 'passed' | 'failed' | ''

    const handleDecision = async (decision) => {
        setSubmitting(decision);
        try {
            await apiClient.setRoundDecision(processId, r.roundNumber, { decision, remark });
            toast.success(`Round ${r.roundNumber} marked as ${decision}.`);
            setShowActions(false);
            onDecisionSaved();
        } catch (err) {
            toast.error(err.message || 'Failed to save decision.');
        } finally {
            setSubmitting('');
        }
    };

    return (
        <Card className="relative overflow-hidden">
            {/* Left colour bar */}
            <div className={cn(
                'absolute left-0 top-0 bottom-0 w-1 rounded-l-lg',
                r.status === 'passed'     && 'bg-green-500',
                r.status === 'failed'     && 'bg-red-500',
                r.status === 'terminated' && 'bg-red-500',
                r.status === 'completed'  && 'bg-blue-400',
                !['passed','failed','completed','terminated'].includes(r.status) && 'bg-muted',
            )} />
            <CardHeader className="pl-5 pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                        {r.roundName || `Round ${r.roundNumber}`}
                        {(r.attemptNumber || 1) > 1 && (
                            <Badge variant="outline" className="text-[10px]">Attempt {r.attemptNumber}</Badge>
                        )}
                        {isCurrentRound && !isTerminal && (
                            <Badge variant="outline" className="text-[10px] border-blue-400/50 text-blue-600">Current</Badge>
                        )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {r.interviewType && (
                            <Badge variant="outline" className="text-xs">{r.interviewType}</Badge>
                        )}
                        {r.difficulty && (
                            <Badge variant="outline" className="text-xs">{r.difficulty}</Badge>
                        )}
                        <Badge variant="outline" className={cn('text-xs', ROUND_STATUS_COLORS[r.status])}>
                            {r.status?.replace(/_/g, ' ')}
                        </Badge>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pl-5 space-y-3">
                {displayScore != null && (
                    <ScoreBar score={displayScore} passMark={r.passMark ?? 70} />
                )}

                {/* Session date + link (passes processId in state for back-nav) */}
                {(dateStr || r.sessionId) && (
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{dateStr}</span>
                        {r.sessionId && (
                            <Link
                                to={`/admin/reports/${r.sessionId}`}
                                state={{ processId }}
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                                View session <ChevronRight className="h-3 w-3" />
                            </Link>
                        )}
                    </div>
                )}

                {/* Existing remark */}
                {r.remark && (
                    <div className="flex items-start gap-1.5 rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
                        <MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary/60" />
                        <span>{r.remark}</span>
                    </div>
                )}

                {/* Round report snapshot */}
                {hasReportData && (
                    <div className="rounded-md bg-muted/40 p-3 space-y-2 text-sm">
                        {r.interviewSummary && (
                            <p className="text-muted-foreground italic">"{r.interviewSummary}"</p>
                        )}
                        {r.strengths?.length > 0 && (
                            <div>
                                <span className="font-medium text-green-600">Strengths: </span>
                                <span className="text-muted-foreground">{r.strengths.slice(0, 2).join(', ')}</span>
                            </div>
                        )}
                        {r.areasForImprovement?.length > 0 && (
                            <div>
                                <span className="font-medium text-amber-600">Improve: </span>
                                <span className="text-muted-foreground">{r.areasForImprovement.slice(0, 2).join(', ')}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Pass / Fail action panel ─────────────────────────── */}
                {canAct && (
                    <div className="border-t pt-3 space-y-2">
                        <button
                            type="button"
                            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => setShowActions(v => !v)}
                        >
                            {showActions ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            Round Decision
                        </button>
                        {showActions && (
                            <div className="space-y-2">
                                <div className="space-y-1">
                                    <Label className="text-xs">Remark (optional)</Label>
                                    <Textarea
                                        value={remark}
                                        onChange={e => setRemark(e.target.value)}
                                        placeholder="Add notes for this round decision…"
                                        rows={2}
                                        className="text-xs resize-none"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-green-500/50 text-green-600 hover:bg-green-500/10 gap-1.5"
                                        disabled={!!submitting}
                                        onClick={() => handleDecision('passed')}
                                    >
                                        {submitting === 'passed'
                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            : <CheckCircle2 className="h-3.5 w-3.5" />}
                                        Pass
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-red-500/50 text-red-600 hover:bg-red-500/10 gap-1.5"
                                        disabled={!!submitting}
                                        onClick={() => handleDecision('failed')}
                                    >
                                        {submitting === 'failed'
                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            : <XCircle className="h-3.5 w-3.5" />}
                                        Fail
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Schedule hints are handled by the top-level ScheduleRoundBanner
                    which uses the backend's schedulableRound — no inline hint needed here. */}
            </CardContent>
        </Card>
    );
}

function ScheduleRoundBanner({ processId, label, variant = 'next' }) {
    const navigate = useNavigate();
    const styles = variant === 'retake'
        ? 'border-amber-500/20 bg-amber-500/5 text-amber-800 dark:text-amber-200'
        : 'border-green-500/20 bg-green-500/5 text-green-700 dark:text-green-400';
    return (
        <div className={cn('flex items-center justify-between rounded-md border px-3 py-2 text-xs gap-2', styles)}>
            <span className="font-medium">{label}</span>
            <Button
                size="sm"
                variant="outline"
                className="h-6 text-[11px] px-2 gap-1"
                onClick={() => navigate(`/admin/schedule?processId=${processId}`)}
            >
                <CalendarPlus className="h-3 w-3" /> {variant === 'retake' ? 'Schedule retake' : 'Schedule'}
            </Button>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ConsolidatedReportPage() {
    const { processId } = useParams();
    const navigate = useNavigate();
    const [process, setProcess] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [decidingStatus, setDecidingStatus] = useState('');
    const [offerOpen, setOfferOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await apiClient.getCandidateProcess(processId);
            setProcess(res.data);
        } catch (err) {
            toast.error(err.message || 'Failed to load process.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [processId]);

    const handleGenerateReport = async () => {
        setGenerating(true);
        try {
            await apiClient.generateConsolidatedReport(processId);
            toast.success('Consolidated report generated.');
            await load();
        } catch (err) {
            toast.error(err.message || 'Failed to generate report.');
        } finally {
            setGenerating(false);
        }
    };

    const handleExportPDF = async () => {
        setExporting(true);
        try {
            const candidateName = process?.candidateId
                ? `${process.candidateId.firstName}_${process.candidateId.lastName}`
                : 'candidate';
            await apiClient.exportConsolidatedPDF(processId, `${candidateName}_consolidated_report.pdf`);
            toast.success('PDF downloaded successfully.');
        } catch (err) {
            toast.error(err.message || 'Failed to export PDF.');
        } finally {
            setExporting(false);
        }
    };

    const handleDecision = async (finalDecision) => {
        if (finalDecision === 'hire') {
            // Open offer letter modal instead of directly setting decision
            setOfferOpen(true);
            return;
        }
        setDecidingStatus(finalDecision);
        try {
            await apiClient.setProcessDecision(processId, { finalDecision });
            toast.success('Decision updated.');
            await load();
        } catch (err) {
            toast.error(err.message || 'Failed to update decision.');
        } finally {
            setDecidingStatus('');
        }
    };

    if (loading) {
        return <ConsolidatedReportSkeleton />;
    }

    if (!process) {
        return (
            <div className="py-24 text-center text-muted-foreground">
                Process not found. <Button variant="link" onClick={() => navigate(-1)}>Go back</Button>
            </div>
        );
    }

    const candidate = process.candidateId;
    const template  = process.templateId;
    const report    = process.consolidatedReport;
    const name      = candidate ? `${candidate.firstName} ${candidate.lastName}` : 'Unknown';
    const resume    = process.resume;

    // ── Blueprint-aware round computations ────────────────────────────────────
    // latestRounds: one entry per roundNumber (deduplicated across retakes).
    // Works correctly with non-sequential old roundNumbers (e.g. Final Round = rn 4).
    const latestRounds = latestPerRoundNumber(process.rounds || []);
    const plannedTotal = process.plannedTotalRounds || latestRounds.length;

    // completedRounds: rounds that have a score from the InterviewReport (overallScore)
    // or the process round entry (score). Use overallScore first — it is enriched from
    // the InterviewReport and is always populated once the interview is finalised.
    const completedRounds = latestRounds.filter(r => (r.overallScore ?? r.score) != null).length;

    const avgScore = completedRounds
        ? Math.round(
            latestRounds
                .filter(r => (r.overallScore ?? r.score) != null)
                .reduce((s, r) => s + (r.overallScore ?? r.score ?? 0), 0)
            / completedRounds
          )
        : null;

    // "Current Round" display — show the round name from the latest scheduled/active round.
    // Falls back to a position-based label so old data (currentRound=4, planned=2) shows correctly.
    const latestRoundEntry = latestRounds.length > 0
        ? latestRounds[latestRounds.length - 1]  // highest roundNumber in process
        : null;
    const currentRoundLabel = latestRoundEntry?.roundName
        || (latestRounds.length > 0 ? `Round ${latestRounds.length}` : '—');

    // ── Hire/Final Decision gate ───────────────────────────────────────────────
    // Show the decision panel only when:
    // 1. The latest attempt for EVERY planned round is in a terminal status, AND
    // 2. ALL planned rounds have been attempted (latestRounds.length >= plannedTotal).
    // This prevents showing "Hire" when only 1 of 2 planned rounds has been completed.
    const allLatestTerminal = latestRounds.length > 0
        && latestRounds.every(r => TERMINAL.has(r.status));
    const allPlannedRoundsDone = latestRounds.length >= plannedTotal;
    const showDecisionPanel = allLatestTerminal && allPlannedRoundsDone
        && ['in_progress', 'passed', 'rejected', 'on_hold'].includes(process.status);

    // Pre-fill for offer letter
    const offerPrefill = {
        candidateName:  name !== 'Unknown' ? name : '',
        role:           template?.position || template?.title || '',
        department:     template?.department || '',
        existingContent: process.offerLetter?.content || null,
        ...(process.offerLetter?.details || {}),
    };

    return (
        <div className="space-y-6 mx-auto">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2 -ml-2 gap-1.5">
                        <ArrowLeft className="h-4 w-4" /> Back
                    </Button>
                    <h1 className="text-2xl font-bold">{name}</h1>
                    <p className="text-muted-foreground">{template?.title || 'Interview Process'}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <Badge variant="outline" className={cn('text-sm', DECISION_COLORS[process.finalDecision])}>
                        {process.finalDecision || 'pending'}
                    </Badge>
                    <span className="text-xs text-muted-foreground capitalize">
                        {process.status?.replace(/_/g, ' ')}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5 text-xs"
                            onClick={handleExportPDF}
                            disabled={exporting}
                        >
                            {exporting
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Download className="h-3.5 w-3.5" />}
                            Export PDF
                        </Button>
                        {process.offerLetter?.content && (
                            <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5 text-xs"
                                onClick={() => setOfferOpen(true)}
                            >
                                <FileText className="h-3.5 w-3.5" /> View Offer Letter
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {process.schedulableRound && (
                <ScheduleRoundBanner
                    processId={processId}
                    label={process.schedulableIsRetake
                        ? `Schedule retake: ${process.schedulableRound.roundName} (attempt ${process.schedulableAttemptNumber})`
                        : `Ready to schedule: ${process.schedulableRound.roundName}`}
                    variant={process.schedulableIsRetake ? 'retake' : 'next'}
                />
            )}
            {!process.schedulableRound && process.schedulableMessage && process.finalDecision !== 'hire' && (
                <div className="rounded-md border border-muted bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                    {process.schedulableMessage}
                </div>
            )}

            {/* Summary strip */}
            <div className="grid gap-4 sm:grid-cols-3">
                <Card>
                    <CardContent className="pt-4">
                        <div className="text-xs text-muted-foreground mb-1">Rounds Completed</div>
                        <div className="text-2xl font-bold">
                            {completedRounds} / {plannedTotal}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4">
                        <div className="text-xs text-muted-foreground mb-1">Average Score</div>
                        <div className="text-2xl font-bold">
                            {report?.overallScore != null ? `${Math.round(report.overallScore)}%` : avgScore != null ? `${avgScore}%` : '—'}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4">
                        <div className="text-xs text-muted-foreground mb-1">Current Round</div>
                        {/* Show round name from blueprint, not raw currentRound number */}
                        <div className="text-xl font-bold">{currentRoundLabel}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            {latestRounds.length} of {plannedTotal} scheduled
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* AI Consolidated Report */}
            {report ? (
                <Card className="border-primary/20 overflow-hidden">
                    {/* Header band */}
                    <div className="bg-primary/5 border-b border-primary/10 px-6 py-4">
                        <div className="flex items-start justify-between flex-wrap gap-3">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <Sparkles className="h-5 w-5 text-primary" />
                                    <span className="text-base font-semibold">AI Consolidated Report</span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Generated {new Date(report.generatedAt).toLocaleString()}
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="text-right">
                                    <div className="text-3xl font-bold tabular-nums">
                                        {Math.round(report.overallScore)}%
                                    </div>
                                    <div className="text-xs text-muted-foreground">Overall Score</div>
                                </div>
                                <Badge
                                    variant="outline"
                                    className={cn('text-sm px-3 py-1 font-semibold', DECISION_COLORS[report.hiringDecision])}
                                >
                                    {report.hiringDecision === 'hire'
                                        ? '✓ Hire'
                                        : report.hiringDecision === 'hold'
                                            ? '⏸ Hold'
                                            : '✗ Reject'}
                                </Badge>
                            </div>
                        </div>
                    </div>

                    <CardContent className="pt-5 space-y-6">
                        {/* AI recommendation */}
                        <div className="rounded-lg bg-muted/40 border border-border/50 p-4">
                            <p className="text-sm leading-relaxed text-foreground">{report.aiRecommendation}</p>
                        </div>

                        {/* Skill Progression */}
                        {report.skillProgression?.length > 0 && (
                            <div>
                                <h4 className="text-sm font-semibold mb-4 flex items-center gap-1.5">
                                    <TrendingUp className="h-4 w-4 text-primary" />
                                    Skill Progression
                                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                                        — per round scores vs 70% pass mark
                                    </span>
                                </h4>

                                <div className="grid gap-3 sm:grid-cols-2">
                                    {report.skillProgression.map((sp, i) => {
                                        const scores = sp.scores || [];
                                        const last   = scores[scores.length - 1] ?? 0;
                                        const first  = scores[0] ?? 0;
                                        const trend  = scores.length > 1
                                            ? (last > first + 3 ? 'up' : last < first - 3 ? 'down' : 'flat')
                                            : null;
                                        return (
                                            <div
                                                key={i}
                                                className="rounded-lg border border-border/60 bg-card p-3 space-y-2.5"
                                            >
                                                {/* Skill name + trend */}
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-sm font-medium leading-tight flex-1 min-w-0">
                                                        {sp.dimension}
                                                    </span>
                                                    {trend === 'up' && (
                                                        <TrendingUp className="h-3.5 w-3.5 text-green-500 shrink-0" />
                                                    )}
                                                    {trend === 'down' && (
                                                        <TrendingDown className="h-3.5 w-3.5 text-red-400 shrink-0" />
                                                    )}
                                                    {trend === 'flat' && (
                                                        <Minus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                                    )}
                                                </div>

                                                {/* Per-round bars */}
                                                <div className="space-y-1.5">
                                                    {scores.map((s, j) => {
                                                        const pct    = Math.min(100, Math.max(0, s ?? 0));
                                                        const passed = s >= 70;
                                                        return (
                                                            <div key={j} className="flex items-center gap-2">
                                                                <span className="text-[11px] text-muted-foreground w-6 shrink-0 font-medium">
                                                                    R{j + 1}
                                                                </span>
                                                                <div className="relative flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                                                    <div
                                                                        className={cn(
                                                                            'h-full rounded-full transition-all duration-500',
                                                                            passed ? 'bg-green-500' : 'bg-red-400'
                                                                        )}
                                                                        style={{ width: `${pct}%` }}
                                                                    />
                                                                    {/* 70% pass mark line */}
                                                                    <div
                                                                        className="absolute top-0 bottom-0 w-px bg-border"
                                                                        style={{ left: '70%' }}
                                                                    />
                                                                </div>
                                                                <span
                                                                    className={cn(
                                                                        'text-[11px] font-semibold tabular-nums w-8 text-right shrink-0',
                                                                        passed ? 'text-green-600' : 'text-red-500'
                                                                    )}
                                                                >
                                                                    {Math.round(s)}%
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Legend */}
                                <div className="mt-3 flex items-center gap-4 text-[11px] text-muted-foreground">
                                    <span className="flex items-center gap-1.5">
                                        <span className="h-2 w-6 rounded-full bg-green-500 inline-block" />
                                        ≥ 70% (pass)
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <span className="h-2 w-6 rounded-full bg-red-400 inline-block" />
                                        &lt; 70% (below pass mark)
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <span className="h-3 w-px bg-border inline-block" />
                                        70% threshold
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Round summaries */}
                        {report.roundSummaries?.length > 0 && (
                            <>
                                <Separator />
                                <div>
                                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                                        <BarChart3 className="h-4 w-4 text-primary" />
                                        Round Summaries
                                    </h4>
                                    <div className="space-y-3">
                                        {report.roundSummaries.map((rs, i) => {
                                            // roundSummaries entries are objects from the backend
                                            const isObj = rs && typeof rs === 'object';
                                            const label    = isObj ? (rs.roundName || `Round ${rs.roundNumber || i + 1}`) : `Round ${i + 1}`;
                                            const score    = isObj ? rs.score : null;
                                            const passMark = isObj ? (rs.passMark ?? 70) : 70;
                                            const summary  = isObj ? rs.interviewSummary : (typeof rs === 'string' ? rs : '');
                                            const strengths   = isObj ? (rs.strengths || []) : [];
                                            const improvements = isObj ? (rs.areasForImprovement || []) : [];
                                            const passed = score != null && score >= passMark;
                                            return (
                                                <div key={i} className="rounded-lg border border-border/60 bg-card p-3 space-y-2">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className={cn(
                                                                'inline-flex items-center justify-center rounded-full text-[11px] font-bold px-2.5 py-0.5',
                                                                score != null
                                                                    ? passed
                                                                        ? 'bg-green-500/15 text-green-700'
                                                                        : 'bg-red-400/15 text-red-600'
                                                                    : 'bg-muted text-muted-foreground'
                                                            )}>
                                                                {label}
                                                            </span>
                                                            {isObj && rs.interviewType && (
                                                                <span className="text-[11px] text-muted-foreground">{rs.interviewType}</span>
                                                            )}
                                                        </div>
                                                        {score != null && (
                                                            <span className={cn(
                                                                'text-sm font-semibold tabular-nums',
                                                                passed ? 'text-green-600' : 'text-red-500'
                                                            )}>
                                                                {Math.round(score)}%
                                                            </span>
                                                        )}
                                                    </div>
                                                    {summary && (
                                                        <p className="text-xs text-muted-foreground leading-relaxed italic">
                                                            "{summary}"
                                                        </p>
                                                    )}
                                                    {(strengths.length > 0 || improvements.length > 0) && (
                                                        <div className="flex gap-4 text-xs">
                                                            {strengths.length > 0 && (
                                                                <div className="flex-1">
                                                                    <span className="font-medium text-green-600">Strengths: </span>
                                                                    <span className="text-muted-foreground">{strengths.slice(0, 2).join(', ')}</span>
                                                                </div>
                                                            )}
                                                            {improvements.length > 0 && (
                                                                <div className="flex-1">
                                                                    <span className="font-medium text-amber-600">Improve: </span>
                                                                    <span className="text-muted-foreground">{improvements.slice(0, 2).join(', ')}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Regenerate button */}
                        <div className="flex justify-end pt-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleGenerateReport}
                                disabled={generating}
                                className="text-muted-foreground text-xs gap-1.5"
                            >
                                {generating
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Sparkles className="h-3.5 w-3.5" />}
                                Regenerate Report
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card className="border-dashed">
                    <CardContent className="py-10 text-center space-y-3">
                        <BarChart3 className="h-8 w-8 text-muted-foreground mx-auto" />
                        <p className="text-sm text-muted-foreground">
                            {completedRounds > 0
                                ? 'Generate an AI consolidated report summarising all completed rounds.'
                                : 'Complete at least one interview round to generate a consolidated report.'}
                        </p>
                        <Button
                            onClick={handleGenerateReport}
                            disabled={generating || completedRounds === 0}
                        >
                            {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            <Sparkles className="mr-1.5 h-4 w-4" />
                            Generate Consolidated Report
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* ── Final Hiring Decision (gated: all rounds terminal) ──────────── */}
            {showDecisionPanel ? (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Final Hiring Decision</CardTitle>
                        <CardDescription>All rounds are complete — confirm your decision for this candidate.</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-wrap gap-3">
                        <Button
                            variant="outline"
                            className="border-green-500/50 text-green-600 hover:bg-green-500/10"
                            disabled={!!decidingStatus}
                            onClick={() => handleDecision('hire')}
                        >
                            <ThumbsUp className="mr-1.5 h-4 w-4" />
                            Hire &amp; Generate Offer Letter
                        </Button>
                        <Button
                            variant="outline"
                            className="border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
                            disabled={!!decidingStatus}
                            onClick={() => handleDecision('on_hold')}
                        >
                            {decidingStatus === 'on_hold' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Clock className="mr-1.5 h-4 w-4" />}
                            Hold
                        </Button>
                        <Button
                            variant="outline"
                            className="border-red-500/50 text-red-600 hover:bg-red-500/10"
                            disabled={!!decidingStatus}
                            onClick={() => handleDecision('reject')}
                        >
                            {decidingStatus === 'reject' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-1.5 h-4 w-4" />}
                            Reject
                        </Button>
                    </CardContent>
                </Card>
            ) : latestRounds.length > 0 && (!allLatestTerminal || !allPlannedRoundsDone) && (
                // Informational banner when rounds aren't fully complete yet
                <Card className="border-amber-200/50 dark:border-amber-800/40 bg-amber-50/20 dark:bg-amber-900/10">
                    <CardContent className="py-4 flex items-center gap-3">
                        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                            {!allPlannedRoundsDone
                                ? `Complete all ${plannedTotal} planned round${plannedTotal !== 1 ? 's' : ''} before making a final hiring decision (${latestRounds.length} of ${plannedTotal} scheduled so far).`
                                : 'Final Hiring Decision will be available once all round results have been marked as passed or failed.'}
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Round-by-round detail */}
            <div>
                <h2 className="text-lg font-semibold mb-3">Round Results</h2>
                {process.rounds.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No rounds scheduled yet.</p>
                ) : (
                    <div className="space-y-3">
                        {[...process.rounds]
                            .sort((a, b) =>
                                a.roundNumber - b.roundNumber || (a.attemptNumber || 1) - (b.attemptNumber || 1)
                            )
                            .map((r) => (
                                <RoundCard
                                    key={`${r.roundNumber}-${r.attemptNumber || 1}-${r.sessionId}`}
                                    round={r}
                                    processId={processId}
                                    onDecisionSaved={load}
                                    isCurrentRound={r.roundNumber === process.currentRound}
                                />
                            ))}
                    </div>
                )}
            </div>

            {/* Offer Letter Modal */}
            <OfferLetterModal
                open={offerOpen}
                onClose={() => setOfferOpen(false)}
                processId={processId}
                prefill={offerPrefill}
                onGenerated={() => {
                    load();
                    toast.success('Offer letter saved to process.');
                }}
            />
        </div>
    );
}
